import React from 'react';
import PropTypes from 'prop-types';
import { Empty, Tag, Progress, Space, Statistic } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, MinusOutlined } from '@ant-design/icons';
import './NivelWidget.scss';

const NivelWidget = ({ data, config }) => {
  if (!data || data.nivelActual === undefined) {
    return <Empty description="No hay datos disponibles" />;
  }

  const isVertical = config.orientacion !== 'horizontal';
  const isPorcentaje = config.modo === 'porcentaje';

  // Obtener color del segmento activo
  const getActiveColor = () => {
    if (data.segmentoActivo?.color) {
      return data.segmentoActivo.color;
    }
    return '#1890ff';
  };

  // Renderizar etiqueta de nivel
  const renderNivelLabel = () => {
    if (isPorcentaje) {
      return `${data.porcentaje}%`;
    }
    return `${data.nivelActual} ${data.unidades || ''}`;
  };

  // Renderizar alerta si existe
  const renderAlerta = () => {
    if (!data.alertaNivel || !data.alertaNivel.activada) return null;

    const alertaConfig = {
      danger: { color: 'error', icon: <ArrowDownOutlined /> },
      warning: { color: 'warning', icon: <MinusOutlined /> },
      aceptable: { color: 'success', icon: <ArrowUpOutlined /> },
    };

    const config = alertaConfig[data.alertaNivel.estado] || alertaConfig.warning;

    return (
      <div className="nivel-alerta">
        <Tag color={config.color} icon={config.icon}>
          {data.alertaNivel.mensaje || 'Alerta de nivel'}
        </Tag>
      </div>
    );
  };

  // Renderizar barra de progreso
  const renderProgress = () => {
    const strokeColor = getActiveColor();

    if (isVertical) {
      return (
        <div className="nivel-progress-vertical">
          <Progress
            type="dashboard"
            percent={data.porcentaje}
            strokeColor={strokeColor}
            format={() => renderNivelLabel()}
            width={200}
            strokeWidth={12}
          />
        </div>
      );
    }

    return (
      <div className="nivel-progress-horizontal">
        <Progress
          percent={data.porcentaje}
          strokeColor={strokeColor}
          format={() => renderNivelLabel()}
          strokeWidth={24}
          status="active"
        />
      </div>
    );
  };

  // Renderizar segmentos
  const renderSegmentos = () => {
    if (!data.segmentos || data.segmentos.length === 0) return null;

    return (
      <div className="nivel-segmentos">
        <div className="segmentos-title">Rangos:</div>
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          {data.segmentos.map((segmento, index) => {
            const isActive = segmento.etiqueta === data.segmentoActivo?.etiqueta;
            const prevLimit = index === 0 ? 0 : data.segmentos[index - 1].hasta;

            return (
              <div
                key={index}
                className={`segmento-item ${isActive ? 'active' : ''}`}
              >
                <div
                  className="segmento-color"
                  style={{ backgroundColor: segmento.color }}
                />
                <div className="segmento-info">
                  <span className="segmento-label">{segmento.etiqueta}</span>
                  <span className="segmento-range">
                    {prevLimit}% - {segmento.hasta}%
                  </span>
                </div>
              </div>
            );
          })}
        </Space>
      </div>
    );
  };

  return (
    <div className={`nivel-widget ${isVertical ? 'vertical' : 'horizontal'}`}>
      {renderAlerta()}

      <div className="nivel-display">
        {renderProgress()}
      </div>

      <div className="nivel-info">
        {data.segmentoActivo && (
          <div className="nivel-estado">
            <Tag color={data.segmentoActivo.color} style={{ fontSize: '13px' }}>
              {data.segmentoActivo.etiqueta}
            </Tag>
          </div>
        )}

        {!isPorcentaje && (
          <div className="nivel-valores">
            <Space size="large">
              <Statistic
                title="Actual"
                value={data.nivelActual}
                suffix={data.unidades}
                valueStyle={{ fontSize: '16px', color: getActiveColor() }}
              />
              <Statistic
                title="Máximo"
                value={data.nivelMaximo}
                suffix={data.unidades}
                valueStyle={{ fontSize: '14px' }}
              />
            </Space>
          </div>
        )}

        {renderSegmentos()}

        {data.ultimaActualizacion && (
          <div className="nivel-timestamp">
            <small className="text-muted">
              Actualizado: {new Date(data.ultimaActualizacion).toLocaleString('es-MX')}
            </small>
          </div>
        )}
      </div>
    </div>
  );
};

NivelWidget.propTypes = {
  data: PropTypes.shape({
    nivelActual: PropTypes.number.isRequired,
    nivelMinimo: PropTypes.number,
    nivelMaximo: PropTypes.number,
    porcentaje: PropTypes.number.isRequired,
    unidades: PropTypes.string,
    modo: PropTypes.oneOf(['porcentaje', 'absoluto']),
    segmentos: PropTypes.arrayOf(
      PropTypes.shape({
        hasta: PropTypes.number,
        color: PropTypes.string,
        etiqueta: PropTypes.string,
        tipo: PropTypes.oneOf(['danger', 'warning', 'aceptable']),
      })
    ),
    segmentoActivo: PropTypes.shape({
      color: PropTypes.string,
      etiqueta: PropTypes.string,
      tipo: PropTypes.string,
    }),
    alertaNivel: PropTypes.shape({
      estado: PropTypes.string,
      activada: PropTypes.bool,
      mensaje: PropTypes.string,
    }),
    ultimaActualizacion: PropTypes.string,
  }).isRequired,
  config: PropTypes.shape({
    titulo: PropTypes.string,
    modo: PropTypes.oneOf(['porcentaje', 'absoluto']),
    orientacion: PropTypes.oneOf(['vertical', 'horizontal']),
  }).isRequired,
};

export default NivelWidget;
